import React, { useContext, useEffect, useState } from "react";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import Table from "./Table";
import Pagination from "../Pagination";
import { useTranslation } from "react-i18next";
import AuthContext from "context/AuthContext";
import helpers from "../../utils/helpers";
import OSearch from "components/reusable/OSearch";
import ODateRangePicker from "components/shared/datePicker/ODateRangePicker";
import dayjs from "dayjs";
 
function PartnerInquiryList() {
  const { t } = useTranslation();
  const { logoutUser, updatePageName } = useContext(AuthContext);
  const [inquiries, setInquiries] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [filterData, setFilterData] = useState({
    searchKey: "",
    startDate: "",
    endDate: "",
    isReset: false,
    isFilter: false,
  });
  const [sort, setSort] = useState({
    sortBy: "createdAt",
    sortType: "desc",
  });
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });
 
  const getInquiries = async () => {
    try {
      const { searchKey, startDate, endDate } = filterData;
      const payload = {
        page,
        pageSize,
        keyword: searchKey || "",
        sortBy: sort.sortBy,
        sortType: sort.sortType,
      };
      if (startDate) payload.startDate = dayjs(startDate).format("YYYY-MM-DD");
      if (endDate) payload.endDate = dayjs(endDate).format("YYYY-MM-DD");
 
      const result = await apiGet(apiPath.getPartnerInquiries, payload);
      if (result?.status === 200) {
        const response = result?.data?.results;
        const resultStatus = result?.data?.success;
        setInquiries(response?.docs || []);
        setPaginationObj({
          ...paginationObj,
          page: helpers?.ternaryCondition(resultStatus, response.page, null),
          pageCount: helpers?.ternaryCondition(resultStatus, response.totalPages, null),
          perPageItem: helpers?.ternaryCondition(resultStatus, response?.docs?.length, null),
          totalItems: helpers?.ternaryCondition(resultStatus, response.totalDocs, null),
        });
      }
    } catch (error) {
      console.error("error in partner inquiry list", error.message);
      if (error.response?.status === 401 || error.response?.status === 409) {
        logoutUser();
      }
    }
  };
 
  useEffect(() => {
    updatePageName(t("PARTNER_INQUIRIES"));
  }, []);
 
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);
 
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }
    setPage(1);
    setFilterData((current) => ({
      ...current,
      searchKey: debouncedSearchTerm || "",
      isReset: false,
      isFilter: !!debouncedSearchTerm,
    }));
  }, [debouncedSearchTerm]);
 
  useEffect(() => {
    getInquiries();
  }, [page, filterData, sort]);
 
  const handlePageClick = (event) => {
    setPage(event.selected + 1);
  };
 
  const handleDateChange = (start, end) => {
    setPage(1);
    setFilterData((current) => ({
      ...current,
      startDate: start || current.startDate || "",
      endDate: end || current.endDate || "",
      isFilter: true,
      isReset: false,
    }));
  };
 
  const clearDateReset = (value) => {
    if (value === false) {
      setFilterData((current) => ({ ...current, isReset: false }));
    }
  };
 
  const handleReset = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setFilterData({
      searchKey: "",
      startDate: "",
      endDate: "",
      isReset: true,
      isFilter: false,
    });
    setPage(1);
  };
 
  return (
    <div>
      <div className="bg-[#F9F9F9]">
        <div className="px-3 py-4">
          <div className="bg-white border border-[#E9EDF9] rounded-lg">
            <form
              className="border-b border-b-[#E3E3E3] px-4 py-3"
              onSubmit={(event) => event.preventDefault()}
            >
              <div className="flex items-center flex-wrap">
                <div className="relative flex items-center mb-3">
                  <OSearch
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    placeholder={t("SEARCH_PARTNER_INQUIRY")}
                  />
                </div>
                <ODateRangePicker
                  handleDateChange={handleDateChange}
                  isReset={filterData?.isReset}
                  setIsReset={clearDateReset}
                />
                <button
                  type="button"
                  onClick={handleReset}
                  className="bg-LightBlue text-sm px-8 ml-3 mb-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue"
                >
                  {t("O_RESET")}
                </button>
              </div>
            </form>
            <Table inquiries={inquiries} paginationObj={paginationObj} sort={sort} setSort={setSort} />
            {paginationObj?.totalItems !== 0 && (
              <Pagination handlePageClick={handlePageClick} options={paginationObj} page={page} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
 
export default PartnerInquiryList;